import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "../../../lib/http";
import { liveNewsOptions } from "../../../lib/liveNews";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchWaitingSubmissions } from "../../preconsultation/services/preconsultationFormsService";
import { NO_WAITING, WaitingSubmissions, waitingByClient } from "../clientNews";

/**
 * Which clients have pre-consultation answers sitting unread — the other half
 * of what My clients flags, and the half the client list cannot answer.
 *
 * A client's own record carries the forms raised for them, so the client's
 * screen knows. The *list* of clients does not carry them, so a row read from
 * the list has no way to know that answers came back this morning — which is
 * exactly the flag that was missing. One read of the forms endpoint answers it
 * for every client at once. See `fetchWaitingSubmissions`.
 *
 * Polled on the same interval as everything else carrying news, and shared by
 * the rail and the list through one query key, so the badge and the rows it
 * leads to cannot disagree.
 *
 * Silent on failure, but not permanently so. A read that fails answers as an
 * empty one — every screen falls back to what the records themselves carry —
 * and the next poll asks again. It used to switch itself off for the session
 * on the first failure of any kind, and that is how the flag went missing on
 * a real screen: one refused call at login, and no form was news until a hard
 * reload, while the "New" beside a client, read off another endpoint in the
 * same hook, went on working.
 *
 * The one failure that does switch it off is a route that is not there. The
 * endpoint was inferred rather than documented — see
 * `API_ENDPOINTS.preconsultation.forms` — and a server that answers 404 or 405
 * to it should cost one refused request, not one a minute for as long as
 * somebody leaves the tab open.
 */

export const WAITING_SUBMISSIONS_QUERY_KEY = [
  "preconsultation",
  "waiting",
] as const;

/**
 * Whether the route has turned out not to exist. Module-level rather than
 * state: every screen holding this hook should give up together, and a remount
 * is not new information.
 */
let unavailable = false;

/**
 * A route that is not served at all, as against one that refused this call.
 * 404 is no such path; 405 is the path with no GET behind it. Anything else —
 * a 401 from a token caught mid-refresh, a 500, a dropped connection — is a
 * call worth making again in a minute.
 */
const routeMissing = (error: unknown): boolean =>
  error instanceof ApiError && (error.status === 404 || error.status === 405);

/** Test-only: the flag outlives a test as surely as any other singleton. */
export const resetWaitingSubmissions = (): void => {
  unavailable = false;
};

export function useWaitingSubmissions(): WaitingSubmissions {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: WAITING_SUBMISSIONS_QUERY_KEY,
    queryFn: async ({ signal }) => {
      try {
        return await fetchWaitingSubmissions(getToken, signal);
      } catch (error) {
        // Not rethrown: an unreadable answer is the same as an empty one to
        // every screen here, and this way the query holds data rather than an
        // error nobody is going to draw. Only a route that is not there stops
        // the asking — see `routeMissing`.
        if (routeMissing(error)) unavailable = true;
        console.warn("pre-consultation forms could not be listed:", error);
        return [];
      }
    },
    enabled: !unavailable,
    ...liveNewsOptions,
  });

  const submissions = query.data;

  return useMemo(
    () => (submissions ? waitingByClient(submissions) : NO_WAITING),
    [submissions]
  );
}

export default useWaitingSubmissions;
